import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const SenioritySelector = ({ selectedSeniority, onSenioritySelect, className = '' }) => {
  const seniorityLevels = [
    {
      value: 'apprentice',
      label: 'Apprentice',
      description: 'Learning the fundamentals, 0-1 years experience',
      icon: 'GraduationCap',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      experience: '0-1 years',
      characteristics: ['Learning basics', 'Supervised work', 'Skill development focus']
    },
    {
      value: 'junior',
      label: 'Junior',
      description: 'Building skills and confidence, 1-3 years experience',
      icon: 'TrendingUp',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      experience: '1-3 years',
      characteristics: ['Growing independence', 'Skill refinement', 'Increased responsibility']
    },
    {
      value: 'mid',
      label: 'Mid-Level',
      description: 'Competent and reliable, 3-7 years experience',
      icon: 'Target',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      experience: '3-7 years',
      characteristics: ['Independent work', 'Mentoring others', 'Project ownership']
    },
    {
      value: 'senior',
      label: 'Senior',
      description: 'Expert level with leadership responsibilities, 7+ years',
      icon: 'Award',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      experience: '7+ years',
      characteristics: ['Technical expertise', 'Team leadership', 'Strategic thinking']
    },
    {
      value: 'executive',
      label: 'Executive',
      description: 'Strategic leadership and organizational impact, 10+ years',
      icon: 'Crown',
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      experience: '10+ years',
      characteristics: ['Organizational strategy', 'Executive decisions', 'Industry influence']
    }
  ];

  const selectOptions = seniorityLevels?.map(level => ({
    value: level?.value,
    label: level?.label,
    description: level?.description
  }));

  const selectedLevel = seniorityLevels?.find(level => level?.value === selectedSeniority);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold text-foreground">Select Your Seniority Level</h2>
        <p className="text-muted-foreground">
          Choose the level that best matches your current experience and responsibilities
        </p>
      </div>
      {/* Select Dropdown */}
      <div className="max-w-md mx-auto">
        <Select
          label="Seniority Level"
          placeholder="Choose your experience level..."
          options={selectOptions}
          value={selectedSeniority}
          onChange={onSenioritySelect}
          searchable={false}
          className="w-full"
        />
      </div>
      {/* Level Details */}
      {selectedLevel && (
        <div className="max-w-2xl mx-auto">
          <div className={`p-6 rounded-lg border-2 border-primary/20 ${selectedLevel?.bgColor}`}>
            <div className="flex items-start space-x-4">
              <div className={`p-3 rounded-lg bg-white shadow-sm`}>
                <Icon name={selectedLevel?.icon} size={24} className={selectedLevel?.color} />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-3">
                  <h3 className="text-xl font-semibold text-foreground">{selectedLevel?.label}</h3>
                  <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-muted-foreground">
                    {selectedLevel?.experience}
                  </span>
                </div>
                <p className="text-muted-foreground mb-4">{selectedLevel?.description}</p>
                
                <div className="space-y-2">
                  <h4 className="font-medium text-foreground">Typical Characteristics:</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {selectedLevel?.characteristics?.map((characteristic, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                        <span className="text-muted-foreground">{characteristic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Level Comparison Grid - Mobile Hidden */}
      <div className="hidden lg:block">
        <h3 className="text-lg font-medium text-foreground text-center mb-4">All Levels Overview</h3>
        <div className="grid grid-cols-5 gap-3">
          {seniorityLevels?.map((level) => {
            const isSelected = selectedSeniority === level?.value;
            return (
              <button
                key={level?.value}
                onClick={() => onSenioritySelect(level?.value)}
                className={`p-4 rounded-lg border-2 text-center transition-all duration-200 hover:shadow-md ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-card hover:border-primary/50'
                }`}
              >
                <div className={`p-2 rounded-lg ${level?.bgColor} mx-auto mb-3 w-fit`}>
                  <Icon name={level?.icon} size={20} className={level?.color} />
                </div>
                <h4 className={`font-medium text-sm mb-1 ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                  {level?.label}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">{level?.experience}</p>
                <div className="space-y-1">
                  {level?.characteristics?.slice(0, 2)?.map((char, index) => (
                    <div key={index} className="text-xs text-muted-foreground">
                      • {char}
                    </div>
                  ))}
                </div>
                {isSelected && (
                  <Icon name="CheckCircle" size={16} className="text-primary mx-auto mt-2" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      {/* Help Text */}
      <div className="max-w-2xl mx-auto text-center">
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Icon name="Info" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Not sure about your level?</span>
          </div>
          <p className="text-sm text-muted-foreground">
            Consider your years of experience, level of independence, and whether you mentor others or make strategic decisions. 
            You can always adjust this later in your profile settings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SenioritySelector;